import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SharedService } from '@app/core';
import { CompareInvoiceService } from '@app/modules/compare-invoice/services';
import { PushToRetailPosService } from '@app/modules/push-to-retail-pos/services';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-admin-common-company-dropdown',
  templateUrl: './admin-common-company-dropdown.component.html',
  styleUrls: ['./admin-common-company-dropdown.component.scss']
})
export class AdminCommonCompanyDropdownComponent implements OnInit {
  //#region properties
  companySearch$ = new Subject<string>();
  companyList: any[] = [];
  selectedCompanyName: any;
  currentUrl: any;
  previousURL: any;
  //#endregion
  constructor(private compareInvoiceService: CompareInvoiceService,
    private sharedService: SharedService,
    public router: Router,
    private pushToRetailPosService: PushToRetailPosService
  ) { }

  ngOnInit(): void {
    // Load persisted state
    const storedList = localStorage.getItem('adminCompanyList');
    if (storedList) {
      this.companyList = JSON.parse(storedList);
    }
    this.selectedCompanyName = localStorage.getItem('adminSelectedCompanyName');
    this.currentUrl = this.router.url;

    // Trigger initial route handling
    this.handleRouteSelection(this.currentUrl);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.previousURL = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
        this.handleRouteSelection(this.currentUrl);
      });

    this.companySearch$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(term => term.length >= 3),
      switchMap((term) => {
        if (this.currentUrl.includes('/push-to-retail-pos')) {
          return this.pushToRetailPosService.getCompanyList({ isPushToRetailPosList: true, search: term });
        }
        return this.compareInvoiceService.getCompanyList(true, term);
      })
    ).subscribe((response: any) => {
      this.companyList = response;
      localStorage.setItem('adminCompanyList', JSON.stringify(this.companyList));
      this.sharedService.setAdminCompanyList(response);
    }, (error) => {
      this.sharedService.errorAlert(error.message);
    });
  }

  onCompanySearch(event: any) {
    this.companySearch$.next(event.term);
  }

  onChange(key: string) {
    localStorage.setItem('adminSelectedCompanyName', this.selectedCompanyName);
    localStorage.setItem('adminCompanyList', JSON.stringify(this.companyList));
    this.sharedService.onCompanyChangeForAdmin({ key: key, value: this.selectedCompanyName });
  }

  handleRouteSelection(url: string) {
    if (!url) return;

    // Ensure companyList is populated from localStorage if empty
    if (!this.companyList?.length) {
      const storedList = localStorage.getItem('adminCompanyList');
      if (storedList) {
        this.companyList = JSON.parse(storedList);
      }
    }

    // Set selectedCompanyName based on active view's stored sync code
    if (url.includes('/push-to-petpooja')) {
      this.selectedCompanyName = localStorage.getItem('pushToPetpoojaCode') || null;
    } else if (url.includes('/push-to-retail-pos')) {
      this.selectedCompanyName = localStorage.getItem('pushToRetailPosCode') || null;
    }

    if (url.includes('/push-to-petpooja/manage-poss-tags')) {
      this.getCompanyId();
    } else if (url.includes('/push-to-petpooja')) {
      if (this.companyList?.length && this.selectedCompanyName !== undefined && this.selectedCompanyName !== null) {
        const company = this.companyList?.find(
          (c: any) => c && (c.id === this.selectedCompanyName || c.restaurantSyncCode === this.selectedCompanyName)
        );

        if (company && company.restaurantSyncCode) {
          this.sharedService.setAdminCompanyList(this.companyList);
          this.selectedCompanyName = company.restaurantSyncCode;
          setTimeout(() => {
            this.onChange('restaurantSyncCode');
          }, 100);
        } else if (this.previousURL) {
          this.selectedCompanyName = null;
          this.companyList = [];
        } else {
          this.selectedCompanyName = null;
          this.companyList = [];
        }
      }
    } else if (url.includes('/push-to-retail-pos/manage-invoice-tags')) {
      if (this.companyList?.length && this.selectedCompanyName != undefined && this.selectedCompanyName !== null) {
        const company = this.companyList?.find(
          (c: any) => c && (c.retailPosSyncCode == this.selectedCompanyName || c.id === this.selectedCompanyName)
        );
        if (company) {
          this.selectedCompanyName = company.id;
          this.sharedService.setAdminCompanyList(this.companyList);
          setTimeout(() => {
            this.onChange('id');
          }, 100);
        } else {
          this.companyList = [];
          this.selectedCompanyName = null;
        }
      } else if (this.previousURL) {
        this.selectedCompanyName = null;
        this.companyList = [];
      }
    } else if (url.includes('/push-to-retail-pos')) {
      if (this.companyList?.length && this.selectedCompanyName !== undefined && this.selectedCompanyName !== null) {
        const company = this.companyList?.find(
          (c: any) => c && (c.id === this.selectedCompanyName || c.retailPosSyncCode === this.selectedCompanyName)
        );
        if (company && company.retailPosSyncCode) {
          this.selectedCompanyName = company.retailPosSyncCode;
          this.sharedService.setAdminCompanyList(this.companyList);
          setTimeout(() => {
            this.onChange('retailPosSyncCode');
          }, 100);
        } else if (this.previousURL) {
          this.selectedCompanyName = null;
          this.companyList = [];
        } else {
          this.selectedCompanyName = null;
          this.companyList = [];
        }
      } else {
        this.selectedCompanyName = null;
        this.companyList = [];
      }
    }
  }

  getCompanyId() {
    if (this.companyList?.length && this.selectedCompanyName !== undefined) {
      const company = this.companyList?.find(
        (c: any) => c && (c.restaurantSyncCode === this.selectedCompanyName || c.id === this.selectedCompanyName)
      );

      if (company) {
        this.selectedCompanyName = company.id;
        this.sharedService.setAdminCompanyList(this.companyList);
        setTimeout(() => {
          this.onChange('id');
        }, 100);
      } else {
        this.companyList = [];
        this.selectedCompanyName = null;
      }
    }
  }

}
